import React, { createContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Auth } from '@aws-amplify/auth';
import { Hub, Logger } from '@aws-amplify/core';
import { useStaticQuery, graphql } from 'gatsby';

const logger = new Logger('AuthContext');

const AuthContext = createContext(null);
export default AuthContext;

async function fetchAndUpdateUserInfo(setAuthStateFunc, sourceCall) {
  try {
    const userInfo = await Auth.currentUserInfo();

    if (userInfo) {
      setAuthStateFunc(userInfo);
    } else {
      setAuthStateFunc(false);
    }
  } catch (err) {
    logger.error(`AuthContext: Called from: ${sourceCall}. Error from Auth.currentUserInfo():`, err);
    setAuthStateFunc(false);
  }
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(null);

  const data = useStaticQuery(graphql`
    query AmplifyAuthConfigQuery {
      site {
        siteMetadata {
          amplifyAuthConfiguration {
            identityPoolId
            region
            userPoolId
            userPoolWebClientId
            authenticationFlowType
            oauth {
              domain
              scope
              redirectSignIn
              redirectSignOut
              responseType
            }
          }
        }
      }
    }
  `);

  const amplifyAuthConfiguration = data?.site?.siteMetadata?.amplifyAuthConfiguration || {};

  useEffect(() => {

    if (!amplifyAuthConfiguration.userPoolId || !amplifyAuthConfiguration.userPoolWebClientId) {
        logger.error("AuthContext: useEffect - CRITICAL: Config missing userPoolId or userPoolWebClientId. Auth will fail.", amplifyAuthConfiguration);
        fetchAndUpdateUserInfo(setAuth, "useEffect_bad_config_fallback");
    } else {
        try {
            Auth.configure(amplifyAuthConfiguration);
        } catch (configError) {
            logger.error('AuthContext: useEffect - Error during Auth.configure():', configError);
            fetchAndUpdateUserInfo(setAuth, "useEffect_configure_error_fallback");
        }
    }

    const hubListenerCancel = Hub.listen('auth', async ({ payload }) => {
      const { event, data: hubData, message } = payload;

      switch (event) {
        case 'configured':
          await fetchAndUpdateUserInfo(setAuth, "Hub_configured_event");
          break;
        case 'signIn':
          await fetchAndUpdateUserInfo(setAuth, "Hub_signIn_event");
          break;
        case 'signOut':
          setAuth(false);
          break;
        case 'signIn_failure':
        case 'tokenRefresh_failure':
        case 'autoSignIn_failure':
          logger.error(`AuthContext: Hub event: "${event}" (Auth Failure). Setting auth state to false.`, { hubData, message });
          setAuth(false);
          break;
        default:
          break;
      }
    });

    if (amplifyAuthConfiguration.userPoolId) {
        fetchAndUpdateUserInfo(setAuth, "useEffect_direct_initial_call");
    } else {
        logger.warn('AuthContext: useEffect - Skipping direct initial fetch due to apparently incomplete config.');
    }

    return () => {
      hubListenerCancel();
    };
  }, [amplifyAuthConfiguration]);

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
